import { HttpClient } from '@angular/common/http';
import { Injectable, InjectionToken, NgZone, inject } from '@angular/core';
import { API_BASE } from '@core/api';
import { AuthService } from '@core/auth';
import { WA_WINDOW } from '@ng-web-apis/common';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  combineLatest,
  distinctUntilChanged,
  fromEvent,
  startWith,
} from 'rxjs';
import {
  PhotoStationLockStateEvent,
  PhotoStationPhotoAddedEvent,
  PhotoStationPhotoRemovedEvent,
  PhotoStationPhotoUpdatedEvent,
} from '../models';

export type PusherConfig = { key: string; cluster: string };

/** This environment's Pusher app. An empty key turns live updates off. */
export const PUSHER_CONFIG = new InjectionToken<PusherConfig>('pusher.config');

/**
 * The part of a pusher-js channel this service uses. Declared here rather than
 * imported, so this library holds no runtime reference to pusher-js — the
 * NativeScript app consumes `@shared/models` too, and the web build of
 * pusher-js expects a browser `window` the moment it loads.
 */
export interface PusherChannel {
  bind(eventName: string, callback: (data?: unknown) => void): unknown;
}

export interface PusherClient {
  readonly connection: {
    readonly state: string;
    bind(eventName: string, callback: (data?: unknown) => void): unknown;
  };
  subscribe(channelName: string): PusherChannel;
  unsubscribe(channelName: string): void;
  disconnect(): void;
}

export type PusherChannelAuthorizationCallback = (
  error: Error | null,
  authData: { auth: string } | null
) => void;

export type PusherClientOptions = {
  cluster: string;
  forceTLS: boolean;
  channelAuthorization: {
    customHandler: (
      params: { socketId: string; channelName: string },
      callback: PusherChannelAuthorizationCallback
    ) => void;
  };
};

/**
 * Creates the pusher-js client — `(key, options) => new Pusher(key, options)`
 * in the web app. A factory rather than the class, so specs never open a
 * socket and the library never imports pusher-js (see {@link PusherChannel}).
 */
export type PusherClientFactory = (
  key: string,
  options: PusherClientOptions
) => PusherClient;

export const PUSHER_CLIENT_FACTORY = new InjectionToken<PusherClientFactory>(
  'pusher.client.factory'
);

/**
 * - `idle`: nothing watches, so there is no connection.
 * - `disabled`: no Pusher key or factory is configured; nothing will arrive.
 * - `connecting`: the first connection is being set up.
 * - `live`: connected and the user's channel is authorized.
 * - `paused`: a view watches but events cannot arrive — connection lost,
 *   channel refused, or nobody logged in. Resuming sends a refetch signal.
 */
export type PhotoStationRealtimeStatus =
  | 'idle'
  | 'disabled'
  | 'connecting'
  | 'live'
  | 'paused';

/**
 * What a watching view is told. Each may mean the item state changed; the
 * view reloads it unless it already shows the change.
 */
export type PhotoStationRealtimeSignal =
  | { type: 'refetch'; cause: 'subscribed' | 'reconnected' }
  | { type: 'photo-added'; event: PhotoStationPhotoAddedEvent }
  | { type: 'photo-updated'; event: PhotoStationPhotoUpdatedEvent }
  | { type: 'photo-removed'; event: PhotoStationPhotoRemovedEvent };

type Watcher = {
  itemId: number;
  emit: (signal: PhotoStationRealtimeSignal) => void;
};

type ViewWatcher = {
  viewId: string;
  emit: (event: PhotoStationLockStateEvent) => void;
};

/**
 * Live photo-station events over one Pusher connection (RIEG-274).
 *
 * The connection exists only while at least one view watches an item or its
 * own lock, so ordinary tabs hold none. It follows the login: any auth change
 * — logout, a failed refresh, or another user logging in, including from
 * another tab — tears it down, and a new one is opened only for the new
 * user's own channels.
 *
 * Photo events arrive on the user's channel. Lock events arrive on a channel
 * per view, which the view watches while it holds a workstation: the server
 * releases that view's lock once Pusher reports the channel vacated, so a
 * closed tab frees its workstation within seconds.
 *
 * Pusher does not replay, so every (re)subscription asks the views to
 * refetch rather than trusting that nothing was missed.
 */
@Injectable({ providedIn: 'root' })
export class PhotoStationRealtimeService {
  private readonly http = inject(HttpClient);
  private readonly apiBase = inject(API_BASE);
  private readonly auth = inject(AuthService);
  private readonly window = inject(WA_WINDOW);
  private readonly zone = inject(NgZone);
  private readonly config = inject(PUSHER_CONFIG, { optional: true });
  private readonly createClient = inject(PUSHER_CLIENT_FACTORY, {
    optional: true,
  });

  private readonly watchers = new Set<Watcher>();
  private readonly viewWatchers = new Set<ViewWatcher>();
  /** View ids whose channel the current client is subscribed to. */
  private readonly subscribedViews = new Set<string>();
  private readonly status = new BehaviorSubject<PhotoStationRealtimeStatus>(
    'idle'
  );

  private authSubscription: Subscription | null = null;
  private client: PusherClient | null = null;
  private channelName: string | null = null;
  private userId: number | null = null;
  private connectionState = 'initialized';
  private everConnected = false;
  private channelSubscribed = false;
  private everSubscribed = false;
  private subscriptionRefused = false;

  /** The highest `state_version` seen per workstation. */
  private readonly stateVersions = new Map<string, number>();

  readonly status$ = this.status.pipe(distinctUntilChanged());

  /**
   * Live signals for the view of `itemId`. The first subscriber opens the
   * connection, the last unsubscribe closes it.
   */
  watch(itemId: number): Observable<PhotoStationRealtimeSignal> {
    return new Observable<PhotoStationRealtimeSignal>((subscriber) => {
      const watcher: Watcher = {
        itemId,
        emit: (signal) => subscriber.next(signal),
      };
      this.watchers.add(watcher);

      if (this.watcherCount() === 1) {
        this.start();
      } else if (this.channelSubscribed) {
        // Its own initial load may predate events the others already got.
        watcher.emit({ type: 'refetch', cause: 'subscribed' });
      }

      return () => {
        this.watchers.delete(watcher);
        if (this.watcherCount() === 0) {
          this.stop();
        }
      };
    });
  }

  /**
   * `lock-state` events of the view `viewId`, through the view's own channel,
   * subscribed for as long as this is. Events naming another view and events
   * older than the newest version seen for their workstation are dropped.
   * Unsubscribing leaves the user channel to the item watchers.
   */
  watchView(viewId: string): Observable<PhotoStationLockStateEvent> {
    return new Observable<PhotoStationLockStateEvent>((subscriber) => {
      const watcher: ViewWatcher = {
        viewId,
        emit: (event) => subscriber.next(event),
      };
      this.viewWatchers.add(watcher);

      if (this.watcherCount() === 1) {
        this.start();
      } else if (this.client && this.userId !== null) {
        this.subscribeView(this.client, this.userId, viewId);
      }

      return () => {
        this.viewWatchers.delete(watcher);
        if (this.watcherCount() === 0) {
          this.stop();
        } else if (!this.isViewWatched(viewId)) {
          this.unsubscribeView(viewId);
        }
      };
    });
  }

  /**
   * Records a `state_version` of `workstation` the view received, from a
   * response or an event, and answers whether it is current. Lock events
   * below the highest version seen are dropped; feeding response versions
   * here keeps a late event from undoing a newer response. Each workstation
   * counts its own versions, whoever holds it.
   */
  noteStateVersion(workstation: string, version: number): boolean {
    if (version < (this.stateVersions.get(workstation) ?? 0)) {
      return false;
    }
    this.stateVersions.set(workstation, version);
    return true;
  }

  private start() {
    if (!this.config?.key || !this.createClient) {
      this.status.next('disabled');
      return;
    }
    // `storage` fires only for writes from other tabs: a login or logout there
    // changes the user this tab's requests run as, without this tab's own
    // authenticated flag ever moving.
    this.authSubscription = combineLatest([
      this.auth.isAuthenticated$,
      fromEvent(this.window, 'storage').pipe(startWith(null)),
    ]).subscribe(([authenticated]) => this.followAuth(authenticated));
  }

  private stop() {
    this.authSubscription?.unsubscribe();
    this.authSubscription = null;
    this.teardown();
    this.userId = null;
    this.status.next('idle');
  }

  private followAuth(authenticated: boolean) {
    const userId = authenticated ? this.currentUserId() : null;
    if (this.client && userId === this.userId) {
      return;
    }
    this.teardown();
    this.userId = userId;
    if (userId === null) {
      this.status.next('paused');
      return;
    }
    this.connect(userId);
  }

  private connect(userId: number) {
    const config = this.config as PusherConfig;
    const createClient = this.createClient as PusherClientFactory;
    const channelName = `private-photo-station.${userId}`;
    this.channelName = channelName;
    this.status.next('connecting');

    // pusher-js keeps timers and a socket running for the connection's whole
    // life; created inside the zone they would keep it unstable and run change
    // detection on every ping.
    const client = this.zone.runOutsideAngular(() =>
      createClient(config.key, {
        cluster: config.cluster,
        forceTLS: true,
        channelAuthorization: {
          customHandler: (params, callback) =>
            this.authorize(params, callback),
        },
      })
    );
    this.client = client;

    const on = <T>(handler: (data: T) => void) => this.guard(client, handler);

    client.connection.bind(
      'state_change',
      on(({ current }: { current: string }) => this.onConnectionState(current))
    );

    const channel = client.subscribe(channelName);
    channel.bind(
      'pusher:subscription_succeeded',
      on(() => {
        this.channelSubscribed = true;
        this.everSubscribed = true;
        this.subscriptionRefused = false;
        this.updateStatus();
        this.broadcast(() => ({ type: 'refetch', cause: 'subscribed' }));
      })
    );
    channel.bind(
      'pusher:subscription_error',
      on(() => {
        this.channelSubscribed = false;
        this.subscriptionRefused = true;
        this.updateStatus();
      })
    );
    // Laravel sends `broadcastAs` names verbatim, so these bind as is.
    channel.bind(
      'photo-added',
      on((event: PhotoStationPhotoAddedEvent) =>
        this.emitForItem(event.item_id, { type: 'photo-added', event })
      )
    );
    channel.bind(
      'photo-updated',
      on((event: PhotoStationPhotoUpdatedEvent) =>
        this.emitForItem(event.item_id, { type: 'photo-updated', event })
      )
    );
    channel.bind(
      'photo-removed',
      on((event: PhotoStationPhotoRemovedEvent) =>
        this.emitForItem(event.item_id, { type: 'photo-removed', event })
      )
    );

    for (const watcher of this.viewWatchers) {
      this.subscribeView(client, userId, watcher.viewId);
    }
  }

  /** Runs `handler` in the zone, unless `client` has been torn down since. */
  private guard<T>(client: PusherClient, handler: (data: T) => void) {
    return (data?: unknown) => {
      if (this.client === client) {
        this.zone.run(() => handler(data as T));
      }
    };
  }

  private subscribeView(client: PusherClient, userId: number, viewId: string) {
    if (this.subscribedViews.has(viewId)) {
      return;
    }
    this.subscribedViews.add(viewId);
    client
      .subscribe(viewChannelName(userId, viewId))
      .bind(
        'lock-state',
        this.guard(client, (event: PhotoStationLockStateEvent) =>
          this.onLockState(viewId, event)
        )
      );
  }

  private unsubscribeView(viewId: string) {
    if (!this.subscribedViews.delete(viewId)) {
      return;
    }
    if (this.client && this.userId !== null) {
      this.client.unsubscribe(viewChannelName(this.userId, viewId));
    }
  }

  private onLockState(viewId: string, event: PhotoStationLockStateEvent) {
    if (
      event.view_id !== viewId ||
      !this.noteStateVersion(event.workstation, event.state_version)
    ) {
      return;
    }
    for (const watcher of this.viewWatchers) {
      if (watcher.viewId === viewId) {
        watcher.emit(event);
      }
    }
  }

  private isViewWatched(viewId: string): boolean {
    return [...this.viewWatchers].some((w) => w.viewId === viewId);
  }

  private watcherCount(): number {
    return this.watchers.size + this.viewWatchers.size;
  }

  private teardown() {
    const client = this.client;
    this.client = null;
    if (client) {
      if (this.channelName) {
        client.unsubscribe(this.channelName);
      }
      if (this.userId !== null) {
        for (const viewId of this.subscribedViews) {
          client.unsubscribe(viewChannelName(this.userId, viewId));
        }
      }
      client.disconnect();
    }
    this.subscribedViews.clear();
    this.channelName = null;
    this.connectionState = 'initialized';
    this.everConnected = false;
    this.channelSubscribed = false;
    this.everSubscribed = false;
    this.subscriptionRefused = false;
  }

  /**
   * Authorizes the private channel through `HttpClient`, so the auth
   * interceptor attaches the bearer token and refreshes it if needed. A channel
   * other than the current user's own or one of their watched views' is
   * refused without asking the server: it can only come from a client that
   * outlived a user switch or a view.
   */
  private authorize(
    { socketId, channelName }: { socketId: string; channelName: string },
    callback: PusherChannelAuthorizationCallback
  ) {
    if (!this.isOwnChannel(channelName)) {
      callback(new Error(`Not the current user's channel: ${channelName}`), null);
      return;
    }
    this.http
      .post<{ auth: string }>(`${this.apiBase}/api/broadcasting/auth`, {
        socket_id: socketId,
        channel_name: channelName,
      })
      .subscribe({
        next: (data) => callback(null, data),
        error: (error) =>
          callback(
            new Error(
              `Channel authorization failed (${error?.status ?? 'unknown'})`
            ),
            null
          ),
      });
  }

  private isOwnChannel(channelName: string): boolean {
    if (channelName === this.channelName) {
      return true;
    }
    const userId = this.userId;
    return (
      userId !== null &&
      [...this.subscribedViews].some(
        (viewId) => viewChannelName(userId, viewId) === channelName
      )
    );
  }

  private onConnectionState(current: string) {
    this.connectionState = current;
    if (current === 'connected') {
      if (this.everConnected) {
        this.broadcast(() => ({ type: 'refetch', cause: 'reconnected' }));
      }
      this.everConnected = true;
    } else {
      // pusher-js drops channel subscriptions with the socket and
      // resubscribes after reconnecting.
      this.channelSubscribed = false;
    }
    this.updateStatus();
  }

  private updateStatus() {
    if (this.connectionState === 'connected' && this.channelSubscribed) {
      this.status.next('live');
    } else if (
      !this.everSubscribed &&
      !this.subscriptionRefused &&
      (this.connectionState === 'initialized' ||
        this.connectionState === 'connecting' ||
        this.connectionState === 'connected')
    ) {
      this.status.next('connecting');
    } else {
      this.status.next('paused');
    }
  }

  private emitForItem(itemId: number, signal: PhotoStationRealtimeSignal) {
    for (const watcher of this.watchers) {
      if (watcher.itemId === itemId) {
        watcher.emit(signal);
      }
    }
  }

  private broadcast(signal: () => PhotoStationRealtimeSignal) {
    for (const watcher of this.watchers) {
      watcher.emit(signal());
    }
  }

  /** `loginWithAccesCode` stores an empty user id, which reads back as 0. */
  private currentUserId(): number | null {
    const userId = this.auth.userId;
    return userId !== null && userId > 0 ? userId : null;
  }
}

function viewChannelName(userId: number, viewId: string): string {
  return `private-photo-station-view.${userId}.${viewId}`;
}
